import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import { Tooltip as ReactTooltip } from "react-tooltip";
import moment from "moment";
import Tooltip from "../../../../parts/tooltip";
import SvgIcon from "../../../../parts/SvgIcon";
import submit from "submit-form";
import { DATE_FORMATS } from "../../../../constants";
import DropdownIcons from "../../../../parts/inputs/dropdown-icons";
import ReactDOMServer from "react-dom/server";
import { getOrganizationCoach } from "../../../../utils/user-helpers";
const { DATE_YEAR_FORMAT } = DATE_FORMATS;

const TeamOverview = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const cycle = locals?.editCycle;
  const theme = locals.activeUser.webAppTheme || "dark";
  const [members, setMembers] = useState(cycle?.members);
  const teamsOverview = locals?.teamsOverview?.result;
  const query = locals?.query;
  const referAsClients = locals?.activeUser?.referAsClients;
  const referAsClientsDisplay = referAsClients
    ? t("athletes.roleArticleClient")
    : t("athletes.roleArticleAthlete");
  const sortItems = [
    {
      title: t("teams.teamOverview.nameAscending"),
      src: "/img/svg/sort-amount-up.svg",
      alt: t("teams.teamOverview.ascAlt"),
      className: "icon-white",
      id: "asc",
    },
    {
      title: t("teams.teamOverview.nameDescending"),
      src: "/img/svg/sort-amount-down.svg",
      alt: t("teams.teamOverview.descAlt"),
      className: "icon-white",
      id: "desc",
    },
  ];

  const getConfig = ({ idx, isCurrentMonth, today, isPreviousMonth }) => {
    let className = "value-Completed";
    if (isCurrentMonth) {
      if (today === idx + 1) className = "value-In Progress";
      else if (today < idx + 1) className = "value-Upcoming";
    } else if (!isPreviousMonth) className = "value-Upcoming";

    return { className };
  };

  const month = moment(query?.month, DATE_YEAR_FORMAT);

  const today = Number(moment().format("D"));
  const currentM = moment().month();
  const currentY = moment().year();
  const activeM = moment(month).month();
  const activeY = moment(month).year();
  const isCurrentMonth = currentM === activeM && activeY === currentY;
  const isPreviousMonth =
    (currentY === activeY && currentM > activeM) || currentY > activeY;
  const currentMonthDays = moment(month).daysInMonth();

  const {
    usersOrganization = {
      organizationActiveUsers: [],
    },
    activeUser,
  } = locals;
  const { organizationActiveUsers = [] } = usersOrganization;

  const updateMonth = (amount) => {
    const url = `/actions/refresh`;

    submit(url, {
      method: "GET",
      body: {
        month: month.clone().add(amount, "months").format(DATE_YEAR_FORMAT),
      },
    });
  };

  const renderCell = (
    schedule,
    idx,
    isCurrentMonth,
    today,
    isPreviousMonth,
    member,
    organizationCoachId,
    isFromOtherOrganizationMember
  ) => {
    if (!schedule) return <td></td>;

    const { className } = getConfig({
      idx,
      isCurrentMonth,
      today,
      isPreviousMonth,
    });

    const cellDate = moment(month).add(idx, "days").format("YYYY-MM-DD");
    const isPullMember = member.userType === "PULL";
    const calendarHref = isPullMember
      ? `/athlete-portal/calendar?athlete=${member.id}&date=${cellDate}&view=day&showBack=true${isFromOtherOrganizationMember ? `&organizationCoach=${organizationCoachId}` : ""}`
      : null;

    const cellLabel = `View ${member.name} calendar for ${moment(month).add(idx, "days").format("MMM D, YYYY")}`;
    const cellContent = (
      <a
        className={className}
        href={calendarHref || undefined}
        onClick={(e) => !calendarHref && e.preventDefault()}
        aria-label={calendarHref ? cellLabel : undefined}
        title={calendarHref ? cellLabel : undefined}
      >
        {" "}
      </a>
    );

    return (
      <td
        data-tooltip-html={ReactDOMServer.renderToStaticMarkup(
          <>
            <div>
              <div>
                <span>
                  <b>{moment(month).add(idx, "days").format("MMM D, YYYY")}</b>
                </span>
              </div>
              <div>
                <span>{member.name}</span>
              </div>
            </div>
          </>
        )}
        data-html={true}
        key={`member-${member.id}-day-${idx}`}
        data-tooltip-id={`member-${member.id}-day-${idx}`}
      >
        {cellContent}
        <ReactTooltip id={`member-${member.id}-day-${idx}`} opacity={1} float />
      </td>
    );
  };

  if (!cycle || !cycle?.members || cycle.members.length < 1) {
    return (
      <div className="no-data-state mt-6">
        <p className="s1">{t("teams.teamOverview.noMembers")}</p>
        <p className="p1">
          {t("teams.teamOverview.noMembersHint", { role: referAsClientsDisplay })}
        </p>
      </div>
    );
  }

  return (
    <div className="teams-overview">
      <header>
        <hgroup>
          <span>
            {" "}
            <SvgIcon
              src="/img/new-icons/tick-gradient.svg"
              noFilterOnLight={true}
            />{" "}
            {t("teams.teamOverview.complete")}
          </span>

          <span className="in-progress">{t("teams.teamOverview.inProgress")}</span>
          <span className="upcoming">{t("teams.teamOverview.upcoming")}</span>
        </hgroup>
        <hgroup>
          <div role="month-handler">
            <div>
              <button onClick={() => updateMonth(-1)}>
                <SvgIcon src="/img/new-icons/arrow-left.svg" alt={t("teams.teamOverview.prevAlt")} />
              </button>
              <div className="labelMonth">{month.format("MMMM YYYY")}</div>
              <button onClick={() => updateMonth(1)}>
                <SvgIcon src="/img/new-icons/arrow-right.svg" alt={t("teams.teamOverview.nextAlt")} />
              </button>
            </div>
          </div>
        </hgroup>
        <hgroup>
          <DropdownIcons
            title={t("teams.teamOverview.sort")}
            items={sortItems}
            action={(sort) => {
              setMembers(
                [...members].sort((a, b) => {
                  if (sort === "asc") {
                    return a.name.localeCompare(b.name);
                  } else {
                    return b.name.localeCompare(a.name);
                  }
                })
              );
            }}
          />
          <a
            role="button"
            data-button-type="outline"
            href={`/actions/refresh?month=${moment().format(DATE_YEAR_FORMAT)}`}
            className="h-[42px]!"
          >
            <span>{t("teams.teamOverview.thisMonth")}</span>
          </a>
        </hgroup>
      </header>

      <section role="table-wrapper">
        <table role="report-table">
          <thead role="report-header">
            <tr>
              <th>{t("teams.teamOverview.name")}</th>

              {Array.from({ length: currentMonthDays }, (_, idx) => (
                <th>{idx + 1}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {members &&
              members.length > 0 &&
              members.map((member) => {
                const memberOverview = teamsOverview?.find(
                  (memberData) => memberData._id === member.id
                );

                const { isFromOtherOrganizationMember, organizationCoachId } =
                  getOrganizationCoach(
                    activeUser,
                    member.id,
                    organizationActiveUsers
                  );

                const hasHref = member.userType === "PULL";
                const href = hasHref
                  ? isFromOtherOrganizationMember
                    ? `/athlete-portal/calendar/?athlete=${member.id}&organizationCoach=${organizationCoachId}`
                    : `/athlete-portal/calendar/?athlete=${member.id}`
                  : undefined;

                const imageContent = (
                  <img
                    src={getAvatarUrl(member, null, theme)}
                    loading="lazy"
                    className="profile-picture"
                    width="100%"
                    height="100%"
                  />
                );
                const nameContent = member.name || member.email;

                return (
                  <tr key={`member-${member.id}`}>
                    <td className="no-left-border">
                      <div className="flex gap-2 items-center">
                        {hasHref ? (
                          <a className="image" href={href}>
                            {imageContent}
                          </a>
                        ) : (
                          <span className="image">{imageContent}</span>
                        )}

                        {hasHref ? (
                          <a className="name" href={href}>
                            {nameContent}
                          </a>
                        ) : (
                          <span className="name">{nameContent}</span>
                        )}
                      </div>
                    </td>
                    {memberOverview.scheduled.map((schedule, idx) =>
                      renderCell(
                        schedule,
                        idx,
                        isCurrentMonth,
                        today,
                        isPreviousMonth,
                        member,
                        organizationCoachId,
                        isFromOtherOrganizationMember
                      )
                    )}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default TeamOverview;
